import type { PaginatedExtendedGroupByQuery, ProjectChartDatapoint } from '@/api/generated/model';
import type { AifiLevel } from '@/domains/pages/ai-proficiency/constants';
import { AIFI_LEVELS } from '@/domains/pages/ai-proficiency/constants';
import { levelSharePercentsFromCounts } from '@/domains/pages/ai-proficiency/utils/ladder';
import {
  computeScorePercentChange,
  type OrgScoreSnapshot,
  type ScorePercentChange,
} from '@/domains/pages/ai-proficiency/utils/map-aggregates';
import { normalizeTrendScore } from '@/domains/pages/shared/chart-axis';

const LEVEL_SET = new Set<string>(AIFI_LEVELS);

export type SegmentSummary = {
  name: string;
  /** Humanized label when legacy tag mapping is active. */
  displayName?: string;
  score: number | null;
  scorePrev: number | null;
  delta: ScorePercentChange;
  userCount: number;
};

export function mapSegmentSummaryToScoreSnapshot(segment: SegmentSummary | undefined): OrgScoreSnapshot | null {
  if (!segment) return null;

  return {
    score: segment.score,
    scorePrev: segment.scorePrev,
    delta: segment.delta,
    totalUsers: segment.userCount,
  };
}

/** Rows from level × segment matrix, scoped to one segment for `mapLevelSnapshots`. */
export function filterLevelRowsForSegment(
  data: PaginatedExtendedGroupByQuery | undefined,
  segmentName: string,
): PaginatedExtendedGroupByQuery | undefined {
  if (!data) return undefined;

  const trimmed = segmentName.trim();
  if (!trimmed) return undefined;

  return {
    ...data,
    data: data.data
      .filter((row) => row.group_name_2?.trim() === trimmed)
      .map((row) => ({
        ...row,
        group_name_2: undefined,
        group_name_3: undefined,
        group_name_4: undefined,
      })),
  };
}

export function mapSegmentSummaries(data: PaginatedExtendedGroupByQuery): SegmentSummary[] {
  return data.data
    .map((row) => {
      const score = row.aifi_score ?? null;
      const scorePrev = row.aifi_score_prev ?? null;
      const delta = computeScorePercentChange(score, scorePrev);

      return {
        name: row.group_name,
        score,
        scorePrev,
        delta,
        userCount: row.n_users ?? 0,
      };
    })
    .filter((segment) => segment.name.trim().length > 0)
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
}

export function mapSegmentScoreSnapshot(data: PaginatedExtendedGroupByQuery): OrgScoreSnapshot {
  const row = data.data[0];
  const score = row?.aifi_score ?? null;
  const scorePrev = row?.aifi_score_prev ?? null;
  const totalUsers = data.total_users ?? row?.n_users ?? row?.total_users ?? 0;
  const delta = computeScorePercentChange(score, scorePrev);

  return {
    score,
    scorePrev,
    delta,
    totalUsers,
  };
}

export function mapSegmentScoresFromChartData(chart: ProjectChartDatapoint): {
  buckets: string[];
  scoresBySegment: Record<string, Array<number | null>>;
} {
  const buckets = chart.x_label_values ?? [];
  const scoresBySegment: Record<string, Array<number | null>> = {};

  for (const dataset of chart.datasets ?? []) {
    const name = dataset.label?.trim();
    if (!name) continue;
    scoresBySegment[name] = dataset.data.map((point) => normalizeTrendScore(point.y));
  }

  return { buckets, scoresBySegment };
}

export function mapSegmentLevelShareMatrix(data: PaginatedExtendedGroupByQuery): Record<string, Record<AifiLevel, number>> {
  const countsBySegment = new Map<string, Map<AifiLevel, number>>();

  for (const row of data.data) {
    const levelName = row.group_name;
    const segmentName = row.group_name_2?.trim();
    if (!segmentName || !LEVEL_SET.has(levelName)) continue;

    const level = levelName as AifiLevel;
    const count = row.n_users ?? 0;

    if (!countsBySegment.has(segmentName)) {
      countsBySegment.set(segmentName, new Map());
    }
    const levelMap = countsBySegment.get(segmentName)!;
    levelMap.set(level, (levelMap.get(level) ?? 0) + count);
  }

  const result: Record<string, Record<AifiLevel, number>> = {};

  for (const [segmentName, levelCounts] of countsBySegment.entries()) {
    result[segmentName] = levelSharePercentsFromCounts((level) => levelCounts.get(level) ?? 0);
  }

  return result;
}
